import { Canvas } from '@react-three/fiber';
import type { Piece, Team } from '@halvard/rules';
import { useState, type CSSProperties } from 'react';
import { BoardGrid } from './BoardGrid.js';
import { cameraPosition, Scene } from './Scene.js';
import {
  CENTER_SUMMIT,
  type BoardHint,
  type CameraView,
  type Square,
  type SummitMark,
} from './types.js';

export interface HalvardBoardProps {
  heights: number[][];
  pieces: readonly Piece[];
  /** Current ply, used to show which pieces are pinned. */
  ply: number;
  hints?: readonly BoardHint[];
  /** Fog of war: squares the viewer can see. Others are drawn dark. Omit for full view. */
  visible?: boolean[][];
  /** Summits to mark. Defaults to the shared summit on e5. */
  summits?: readonly SummitMark[];
  selectedId?: string | null;
  /** Keyboard cursor. Shown in 3D while the board has keyboard focus. */
  cursor: Square;
  /** Squares of the last move, outlined faintly. */
  lastMove?: readonly Square[];
  /** Camera preset; changing it moves the camera. */
  view: CameraView;
  /** The side the local player sits on; sets the top view and the arrow keys. */
  facing: Team;
  reducedMotion?: boolean;
  background?: string;
  /** Accessible name of the board grid. */
  label?: string;
  onSquareActivate?: (square: Square) => void;
  onCursorChange?: (square: Square) => void;
  onCancel?: () => void;
  className?: string;
  style?: CSSProperties;
}

/**
 * The complete board: a three.js scene plus an accessible grid for keyboards and screen readers.
 * It renders only what it is given and reports clicks and key presses through callbacks.
 */
export function HalvardBoard(props: HalvardBoardProps) {
  const [focused, setFocused] = useState(false);
  const hints = props.hints ?? [];
  const selected = props.pieces.find((p) => p.alive && p.id === props.selectedId) ?? null;
  const activate = (square: Square) => {
    props.onCursorChange?.(square);
    props.onSquareActivate?.(square);
  };
  const initial = cameraPosition(props.view, props.facing);

  return (
    <div
      className={props.className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        borderRadius: 12,
        ...props.style,
      }}
    >
      <Canvas
        shadows
        flat
        dpr={[1, 2]}
        camera={{ fov: 40, position: [initial.x, initial.y, initial.z], near: 0.1, far: 100 }}
        aria-hidden="true"
      >
        <color attach="background" args={[props.background ?? '#142019']} />
        <Scene
          heights={props.heights}
          pieces={props.pieces}
          ply={props.ply}
          hints={hints}
          visible={props.visible ?? null}
          summits={props.summits ?? CENTER_SUMMIT}
          selected={selected ? { r: selected.r, c: selected.c } : null}
          cursor={focused ? props.cursor : null}
          lastMove={props.lastMove ?? []}
          view={props.view}
          facing={props.facing}
          reducedMotion={props.reducedMotion ?? false}
          onSquareClick={(r, c) => activate({ r, c })}
        />
      </Canvas>
      {focused && (
        // Visible keyboard focus, drawn above the canvas.
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            border: '3px solid #F1EDE2',
            borderRadius: 12,
            pointerEvents: 'none',
          }}
        />
      )}
      <BoardGrid
        heights={props.heights}
        pieces={props.pieces}
        ply={props.ply}
        hints={hints}
        visible={props.visible ?? null}
        summits={props.summits ?? CENTER_SUMMIT}
        selectedId={props.selectedId ?? null}
        cursor={props.cursor}
        facing={props.facing}
        label={props.label ?? 'Board'}
        onCursorChange={(square) => props.onCursorChange?.(square)}
        onActivate={activate}
        onCancel={() => props.onCancel?.()}
        onFocusChange={setFocused}
      />
    </div>
  );
}
